// utils/crypto/pop.ts 

// Proof-of-Possession helpers (current-device uses fingerprint-based device_id)

import { requestPopChallenge, verifyPop } from "../../services/crypto/deviceKeyApi";
import { getDeviceIdAsync } from "../keyManager";
import { getPrivateCryptoKey, migrateFromLocalStorageIfNeeded } from "./keyStorageSecure";

export type PopResult =
  | "ok"             // verified Now
  | "already"        // server says already verified
  | "expired"        // challenge expired / invalid
  | "rate_limited"   // too many attempts
  | "no_private_key" // no private key on this device
  | "decrypt_failed" // RSA decrypt failed (stale payload/rotated key)
  | "network_error"  // network/server error
  | "unexpected";

const b64ToBytes = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
const bytesToB64 = (arr: Uint8Array) => btoa(String.fromCharCode(...arr));

/**
 * PoP using server-provided payload (when available).
 * If payload is missing/invalid, falls back to the standard challenge flow.
 */
export async function performPopWithPayload(
  pop?: { ciphertext_b64?: string } | null
): Promise<PopResult> {
  if (!pop?.ciphertext_b64) return await performPopVerification();

  try {
    // Ensure canonical device_id (derived from key) is available
    let devId: string;
    try {
      devId = await getDeviceIdAsync();
    } catch {
      // No key → cannot prove possession
      return "no_private_key";
    }

    // Ensure private key exists
    let prv: CryptoKey;
    try {
      prv = await getPrivateCryptoKey();
    } catch {
      return "no_private_key";
    }

    // Try decrypting the provided payload once
    let nonceBuf: ArrayBuffer | null = null;
    try {
      const ct = b64ToBytes(pop.ciphertext_b64);
      nonceBuf = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, prv, ct);
    } catch {
      // If decrypt fails, request a fresh challenge and retry once
      try {
        const { pop: fresh, verified } = await requestPopChallenge(devId);
        if (verified) return "already";
        if (!fresh?.ciphertext_b64) return "decrypt_failed";

        const ct2 = b64ToBytes(fresh.ciphertext_b64);
        nonceBuf = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, prv, ct2);
      } catch {
        return "decrypt_failed";
      }
    }

    // Submit nonce back for verification
    try {
      const nonce_b64 = bytesToB64(new Uint8Array(nonceBuf!));
      await verifyPop(devId, nonce_b64);
      return "ok";
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 429) return "rate_limited";
      if (status === 400) return "expired";
      return "network_error";
    }
  } catch {
    return "network_error";
  }
}

/** Thin wrapper: boolean success for legacy call sites. */
export async function performPopWithPayloadBoolean(
  pop?: { ciphertext_b64?: string } | null
): Promise<boolean> {
  const r = await performPopWithPayload(pop);
  return r === "ok" || r === "already";
}

/** Standard flow when no payload is available. */
export async function performPopVerification(): Promise<PopResult> {
  try {
    await migrateFromLocalStorageIfNeeded();

    // Ensure canonical device_id after potential migration
    let devId: string;
    try {
      devId = await getDeviceIdAsync();
    } catch {
      return "no_private_key";
    }

    const { pop, verified } = await requestPopChallenge(devId);
    if (verified) return "already";
    if (!pop?.ciphertext_b64) return "unexpected";

    // Ensure private key exists
    let prv: CryptoKey;
    try {
      prv = await getPrivateCryptoKey();
    } catch {
      return "no_private_key";
    }

    let nonceBuf: ArrayBuffer | null = null;
    try {
      const ct = b64ToBytes(pop.ciphertext_b64);
      nonceBuf = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, prv, ct);
    } catch {
      // one lightweight retry
      try {
        const retry = await requestPopChallenge(devId);
        if (retry.verified) return "already";
        if (!retry.pop?.ciphertext_b64) return "decrypt_failed";
        const ct2 = b64ToBytes(retry.pop.ciphertext_b64);
        nonceBuf = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, prv, ct2);
      } catch {
        return "decrypt_failed";
      }
    }

    try {
      const nonce_b64 = bytesToB64(new Uint8Array(nonceBuf!));
      await verifyPop(devId, nonce_b64);
      return "ok";
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 429) return "rate_limited";
      if (status === 400) return "expired";
      return "network_error";
    }
  } catch {
    return "network_error";
  }
}
